/**
 * The chat session: the transcript the panel shows, the message history the model sees,
 * and the loop between them — stream a reply, run the tools it asks for, send the results
 * back, until the agent stops. Plain observable like the editor store; one per browser tab,
 * reset when another project opens.
 */
import type { Scene } from '../core/hp/types';
import { store, type DocumentScope } from '../state/store';
import { loadAgentCatalog, loadAgentMaterials, type Catalog } from './agentCatalog';
import type { MaterialEntry } from '../state/materials';
import { agentModel, allAgents, BUILT_IN_AGENTS, buildSystemBlocks, findAgent, loadAgentSettings, LOCAL_AGENT_URL, saveAgentSettings, type AgentDefinition, type AgentSettings, type ProviderKind } from './agents';
import { appAccess, editorAccess, type AppAccess, type EditorAccess } from './editorAccess';
import { ClaudeProvider, describeProviderError, MockProvider, PROXY_BASE_PATH, type AgentProvider, type ContentBlockParam, type Message, type MessageParam, type ToolParam, type ToolResultBlockParam } from './providers';
import { renderThroughService } from './renderTask';
import { DEEPSEEK_BASE_URL, DEEPSEEK_MODELS, OpenAICompatibleProvider, ProviderHttpError } from './openaiProvider';
import { agentCursor } from './cursor';
import { briefScene } from './sceneSummary';
import { abortable, captureViewImage, runTool, type RenderOutcome, type RenderRequestInput, type ToolContent, type ToolOutcome, type ToolServices } from './toolRuntime';
import { toolsInGroups } from './toolSchemas';
import { catalogLookup, planSimulation, runSimulation, simulationAnswer, type CatalogLookup } from './simulation';
import { DEMO_AGENTS, demoBots, type DemoTask } from '../collaboration/demoBots';
import { firstMaterialCursor, loadMaterialPage, MATERIAL_SECTIONS } from '../state/materials';
import { formatNumber, plural, t, type TranslationKey } from '../i18n/I18n';

export type ToolStatus = 'pending' | 'running' | 'done' | 'error';
export type ChatPart =
  | { kind: 'text'; text: string; agent?: string }
  | { kind: 'thinking'; text: string; agent?: string }
  | { kind: 'tool'; id: string; name: string; input: unknown; status: ToolStatus; summary?: string; detail?: string; image?: string; agent?: string; external?: boolean;
      /** What the step says while it runs, when the tool's own words (TOOL_PROGRESS) do not fit: the simulation's steps. */
      progress?: string };

export interface ChatTurn {
  id: string;
  role: 'user' | 'assistant' | 'external';
  agentId?: string;
  agentName?: string;
  parts: ChatPart[];
  /** Pictures the user attached, as data URLs. */
  attachments?: string[];
  /** The document before the answer, for rolling the whole answer back. */
  snapshot?: Scene | null;
  snapshotProjectId?: string;
  snapshotScope?: DocumentScope | null;
  lastOwnRevision?: number;
  rollbackInvalidated?: boolean;
  rollbackReason?: 'changed' | 'rename' | 'external';
  rollbackNote?: string;
  rolledBack?: boolean;
  /** Undo steps the answer produced. */
  edits?: number;
  /** The steps stay open under the finished answer: a health check is read step by step. */
  expanded?: boolean;
  error?: { message: string; retryable: boolean; notConfigured?: boolean };
  status: 'streaming' | 'done' | 'error' | 'stopped';
  startedAt: number;
  finishedAt?: number;
}

export type BridgeStatus = { state: 'off' | 'connecting' | 'connected' | 'error'; detail?: string };
/** The local agent server (`npm run agent:local`), as last probed. */
export type LocalAgentStatus = { state: 'off' | 'checking' | 'ready' | 'offline'; detail?: string; project?: string | null };
/** What the editor's proxy reports about the keys it holds (never the keys themselves). */
export type ProxyConfig = { anthropic: boolean; openai: { configured: boolean; baseUrl?: string | null; model?: string | null } } | null;

/** What `POST /run` streams, one JSON object per SSE data line — the server's RunEvent. */
export type LocalRunEvent =
  | { type: 'started'; sessionId?: string; model?: string }
  | { type: 'text'; text: string }
  | { type: 'thinking'; text: string }
  | { type: 'tool_use'; id: string; name: string; input: unknown }
  | { type: 'tool_result'; id: string; isError: boolean; text: string }
  | { type: 'result'; sessionId?: string; isError: boolean; text: string; durationMs?: number; numTurns?: number }
  | { type: 'error'; message: string };

/** Server-sent events as they arrive; stops on abort. */
export async function* readSse(body: ReadableStream<Uint8Array>, signal?: AbortSignal): AsyncGenerator<LocalRunEvent> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  const stop = () => { void reader.cancel(); };
  signal?.addEventListener('abort', stop, { once: true });
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const chunks = buffer.split('\n\n');
      buffer = chunks.pop() ?? '';
      for (const chunk of chunks) {
        const data = chunk.split('\n').filter(line => line.startsWith('data:')).map(line => line.slice(5).trim()).join('');
        if (!data) continue;
        try { yield JSON.parse(data) as LocalRunEvent; } catch { /* a torn line: skip it */ }
      }
    }
  } finally { signal?.removeEventListener('abort', stop); }
}

/** A conversation kept for the chats list: the transcript without document snapshots and pictures, and the model's messages. */
export interface SavedChat { id: string; title: string; preview: string; createdAt: number; updatedAt: number; turns: ChatTurn[]; history: MessageParam[] }

export interface AgentSessionState {
  projectId: string;
  turns: ChatTurn[];
  /** The saved chat the transcript belongs to; null until the first request of a new chat is saved. */
  chatId: string | null;
  /** This project's chats, newest first (`hp.agent.chats.<project>` in this browser). */
  chats: SavedChat[];
  /** What the column shows: the transcript, or the list of chats. */
  view: 'chat' | 'list';
  status: 'idle' | 'running';
  settings: AgentSettings;
  bridge: BridgeStatus;
  local: LocalAgentStatus;
  proxy: ProxyConfig;
  open: boolean;
  draft: string;
}

export interface AgentSessionOptions {
  provider?: (agent: AgentDefinition, settings: AgentSettings) => AgentProvider;
  catalog?: () => Promise<Catalog>;
  materials?: () => Promise<MaterialEntry[]>;
  editor?: () => EditorAccess | null;
  app?: () => AppAccess | null;
  render?: (input: RenderRequestInput, signal?: AbortSignal) => Promise<RenderOutcome>;
  storage?: Pick<Storage, 'getItem' | 'setItem'> | null;
  /** The fetch the local-agent path uses; tests hand in a scripted one. */
  fetch?: typeof fetch;
  maxIterations?: number;
  /** Text streaming pace of the scripted stand-in, ms per chunk. */
  mockPace?: number;
  /** The bots the simulation sends to work and the health check calls; tests hand in their own. */
  demo?: { lease(): () => void; ready(timeout?: number): Promise<{ direct(botId: string, task: DemoTask | null): boolean } | null> };
  /** The catalog as the simulation and the health check ask it. */
  catalogLookup?: CatalogLookup;
  /** Scales the simulation's step times: 1 in the chat, a tiny one in tests. */
  simulationPace?: number;
  /** How long the bots a run called stay after it, ms. */
  botsLinger?: number;
}

export const MAX_ITERATIONS = 40;
const MAX_TOKENS = 16000;
const ROLLBACK_NOTES = {
  changed: '#AgentRollbackChanged', rename: '#AgentRollbackRename', external: '#AgentRollbackExternal', running: '#AgentRollbackRunning', busy: '#AgentRollbackBusy',
} satisfies Record<string, TranslationKey>;
/** Why an answer is not rolled back, in the interface language. */
const rollbackNote = (reason: keyof typeof ROLLBACK_NOTES): string => t(ROLLBACK_NOTES[reason]);
type Listener = () => void;
type ChatStorage = Pick<Storage, 'getItem' | 'setItem'> | null;

const CHATS_KEY = (projectId: string) => `hp.agent.chats.${projectId}`;
const MAX_CHATS = 30;
function loadChats(storage: ChatStorage, projectId: string): SavedChat[] {
  try {
    const raw = storage?.getItem(CHATS_KEY(projectId));
    const chats = raw ? JSON.parse(raw) as SavedChat[] : [];
    return Array.isArray(chats) ? chats.filter(chat => chat && typeof chat.id === 'string' && Array.isArray(chat.turns)) : [];
  } catch { return []; }
}
function saveChats(storage: ChatStorage, projectId: string, chats: SavedChat[]): void {
  try { storage?.setItem(CHATS_KEY(projectId), JSON.stringify(chats)); } catch { /* blocked or full storage: the list is a convenience of this browser */ }
}
/** «Расставь мебель в самой большой комнате» → the chat's title: the first request, cut at a word. */
export function chatTitle(text: string): string {
  const clean = text.replace(/\s+/g, ' ').trim();
  if (clean.length <= 48) return clean || t('#AgentNewChat');
  return clean.slice(0, 47).replace(/\s+\S*$/, '') + '…';
}
/** The last words of the chat, for its row in the list. */
function chatPreview(turns: ChatTurn[]): string {
  for (let index = turns.length - 1; index >= 0; index--) {
    const text = turns[index].parts.filter((part): part is Extract<ChatPart, { kind: 'text' }> => part.kind === 'text').map(part => part.text).join(' ').replace(/\s+/g, ' ').trim();
    if (text) return text.length > 120 ? text.slice(0, 119).replace(/\s+\S*$/, '') + '…' : text;
  }
  return '';
}
/** What a turn keeps between sessions: no document snapshot, no pictures, no rollback (the document has moved on). */
function storableTurn(turn: ChatTurn): ChatTurn {
  return { id: turn.id, role: turn.role, agentId: turn.agentId, agentName: turn.agentName, status: turn.status === 'streaming' ? 'stopped' : turn.status, expanded: turn.expanded,
    startedAt: turn.startedAt, finishedAt: turn.finishedAt, error: turn.error, rolledBack: turn.rolledBack,
    parts: turn.parts.map(part => part.kind === 'tool' ? { ...part, image: undefined } : part) };
}
/** The model's messages without image bytes: a picture is a note once the chat is reopened. */
function storableHistory(history: MessageParam[]): MessageParam[] {
  const textOnly = (block: ContentBlockParam): ContentBlockParam => block.type === 'image' ? { type: 'text', text: '[image]' }
    : block.type === 'tool_result' && Array.isArray(block.content) ? { ...block, content: block.content.map(inner => inner.type === 'image' ? { type: 'text', text: '[image]' } : inner) } : block;
  return history.map(message => typeof message.content === 'string' ? message : { ...message, content: message.content.map(textOnly) });
}

const newId = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : Math.random().toString(36).slice(2));
const nowMs = () => Date.now();
/** «0,4 с», «2,1 с»: how long a check took. */
const seconds = (ms: number) => t('#AgentSeconds', formatNumber(Math.max(ms, 1) / 1000, 1));
const reason = (error: unknown) => error instanceof Error ? error.message : String(error);

type ToolResultContent = Exclude<ToolResultBlockParam['content'], undefined>;
function toApiContent(content: ToolContent[]): ToolResultContent {
  if (content.every(block => block.type === 'text')) return content.map(block => (block as { text: string }).text).join('\n') || 'OK';
  return content.map(block => block.type === 'text' ? { type: 'text', text: block.text } : { type: 'image', source: { type: 'base64', media_type: block.mediaType, data: block.data } });
}
const dataUrlOf = (content: ToolContent[]): string | undefined => {
  const image = content.find((block): block is Extract<ToolContent, { type: 'image' }> => block.type === 'image');
  return image ? `data:${image.mediaType};base64,${image.data}` : undefined;
};
const textOf = (message: Message): string => message.content.filter(block => block.type === 'text').map(block => (block as { text: string }).text).join('\n').trim();

export class AgentSession {
  private state: AgentSessionState;
  private listeners = new Set<Listener>();
  private history: MessageParam[] = [];
  private abort: AbortController | null = null;
  private lastRequest: { text: string; attachments: string[] } | null = null;
  private context: Promise<{ catalog: Catalog; materials: MaterialEntry[] }> | null = null;

  private readonly storage: ChatStorage;
  private persistTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly options: AgentSessionOptions = {}) {
    const storage = options.storage === undefined ? (typeof localStorage === 'undefined' ? null : localStorage) : options.storage;
    this.storage = storage;
    const projectId = store.get().projectId;
    this.state = { projectId, turns: [], chatId: null, chats: loadChats(storage, projectId), view: 'chat', status: 'idle', settings: loadAgentSettings(storage), bridge: { state: 'off' }, local: { state: 'off' }, proxy: null, open: false, draft: '' };
    store.subscribe(() => {
      const projectId = store.get().projectId;
      if (projectId !== this.state.projectId) this.reset(projectId);
      else if (!store.documentBusy && this.state.turns.some(turn => turn.rollbackNote && !turn.rollbackInvalidated)) this.set({});
    });
    store.subscribeDocumentCommits(event => {
      if (!this.state.turns.some(turn => turn.snapshot && !turn.rolledBack)) return;
      this.set({ turns: this.state.turns.map(turn => !turn.snapshot || turn.rolledBack ? turn : event.turnId === turn.id
        ? { ...turn, edits: (turn.edits ?? 0) + 1, lastOwnRevision: event.scope.revision,
          ...(event.undoable === false ? { rollbackInvalidated: true, rollbackReason: 'rename' as const, rollbackNote: rollbackNote('rename') } : {}) }
        : { ...turn, rollbackInvalidated: true, rollbackReason: turn.rollbackReason ?? (turn.id === this.localRunTurn ? 'external' : 'changed'),
          ...(turn.rollbackNote || turn.id === this.localRunTurn ? { rollbackNote: rollbackNote(turn.rollbackReason ?? (turn.id === this.localRunTurn ? 'external' : 'changed')) } : {}) }) });
    });
    // A page learns at once which keys its server holds, so a first-time visitor needs no settings.
    if (typeof window !== 'undefined' && !options.fetch) void this.probeProxyConfig();
  }

  /** Unbound on purpose: `useSyncExternalStore(session.subscribe, session.get)` passes both around as plain functions. */
  get = (): AgentSessionState => this.state;
  subscribe = (fn: Listener): (() => void) => { this.listeners.add(fn); return () => { this.listeners.delete(fn); }; };
  private set(patch: Partial<AgentSessionState>): void {
    this.state = { ...this.state, ...patch };
    this.state.turns = this.state.turns.map(turn => turn.rollbackNote && !turn.rollbackInvalidated
      ? { ...turn, rollbackNote: this.state.status === 'running' ? rollbackNote('running') : store.documentBusy ? rollbackNote('busy') : undefined } : turn);
    for (const fn of this.listeners) fn();
    if (patch.turns && this.state.turns.some(turn => turn.role === 'user')) this.schedulePersist();
  }
  /** The chat goes to this browser's list a moment after it changes; a stream saves at most twice a second. */
  private schedulePersist(): void {
    if (!this.storage || this.persistTimer) return;
    this.persistTimer = setTimeout(() => { this.persistTimer = null; this.persist(); }, 400);
  }
  private persist(): void {
    const turns = this.state.turns, first = turns.find(turn => turn.role === 'user');
    if (!this.storage || !first) return;
    const id = this.state.chatId ?? newId();
    const existing = this.state.chats.find(chat => chat.id === id), last = turns[turns.length - 1];
    const saved: SavedChat = { id, title: existing?.title ?? chatTitle(first.parts.find((part): part is Extract<ChatPart, { kind: 'text' }> => part.kind === 'text')?.text ?? ''),
      preview: chatPreview(turns), createdAt: existing?.createdAt ?? first.startedAt, updatedAt: last?.finishedAt ?? last?.startedAt ?? nowMs(),
      turns: turns.map(storableTurn), history: storableHistory(this.history) };
    const chats = [saved, ...this.state.chats.filter(chat => chat.id !== id)].slice(0, MAX_CHATS);
    saveChats(this.storage, this.state.projectId, chats);
    this.state = { ...this.state, chatId: id, chats };
    for (const fn of this.listeners) fn();
  }
  private patchTurn(id: string, update: (turn: ChatTurn) => ChatTurn): void {
    this.set({ turns: this.state.turns.map(turn => turn.id === id ? update(turn) : turn) });
  }

  /** Another project: a fresh conversation and that project's chats; the settings and the bridge stay. */
  private reset(projectId: string): void {
    this.stop();
    this.history = [];
    this.localSessions.clear();
    this.lastRequest = null;
    if (this.persistTimer) { clearTimeout(this.persistTimer); this.persistTimer = null; }
    this.set({ projectId, turns: [], chatId: null, chats: loadChats(this.storage, projectId), view: 'chat', status: 'idle' });
  }

  clear(): void { this.newChat(); }
  /** The list's «Новый чат»: an empty transcript; the chat left behind stays in the list. */
  newChat(): void {
    this.stop();
    if (this.persistTimer) { clearTimeout(this.persistTimer); this.persistTimer = null; this.persist(); }
    this.history = [];
    this.localSessions.clear();
    this.lastRequest = null;
    this.set({ turns: [], chatId: null, view: 'chat', status: 'idle', draft: '' });
  }
  /** A chat from the list: its transcript and the model's messages come back; the document's rollbacks do not. */
  openChat(id: string): void {
    const chat = this.state.chats.find(candidate => candidate.id === id);
    if (!chat) return;
    if (this.state.chatId === id && this.state.turns.length) { this.set({ view: 'chat' }); return; }
    this.stop();
    if (this.persistTimer) { clearTimeout(this.persistTimer); this.persistTimer = null; this.persist(); }
    this.history = structuredClone(chat.history);
    this.localSessions.clear();
    this.lastRequest = null;
    this.set({ turns: chat.turns.map(turn => ({ ...turn })), chatId: id, view: 'chat', status: 'idle' });
  }
  deleteChat(id: string): void {
    const chats = this.state.chats.filter(chat => chat.id !== id);
    saveChats(this.storage, this.state.projectId, chats);
    if (this.state.chatId === id) { this.stop(); this.history = []; this.lastRequest = null; this.set({ chats, chatId: null, turns: [], status: 'idle' }); }
    else this.set({ chats });
  }
  renameChat(id: string, title: string): void {
    const clean = title.replace(/\s+/g, ' ').trim();
    if (!clean) return;
    const chats = this.state.chats.map(chat => chat.id === id ? { ...chat, title: clean } : chat);
    saveChats(this.storage, this.state.projectId, chats);
    this.set({ chats });
  }
  setView(view: 'chat' | 'list'): void { if (view !== this.state.view) this.set({ view }); }
  setOpen(open: boolean): void { if (open !== this.state.open) this.set({ open }); }
  setDraft(draft: string): void { if (draft !== this.state.draft) this.set({ draft }); }
  setBridgeStatus(bridge: BridgeStatus): void { this.set({ bridge }); }

  /** Conversation ids Claude Code keeps per agent, so a follow-up resumes the same run. */
  private localSessions = new Map<string, string>();
  /** The assistant turn a local run is filling right now; bridge calls attach to it. */
  private localRunTurn: string | null = null;
  get localAgentUrl(): string { return (this.state.settings.localAgentUrl || LOCAL_AGENT_URL).replace(/\/$/, ''); }

  /**
   * The API the built-in agents use: the user's pick when they made one, else whatever the
   * server has a key for — an OpenAI-compatible key alone means DeepSeek (or the configured
   * provider) without anyone touching the settings.
   */
  effectiveProvider(): ProviderKind {
    const settings = this.state.settings;
    if (settings.providerChosen) return settings.provider;
    if (settings.openai?.apiKey && !settings.apiKey) return 'openai';
    const proxy = this.state.proxy;
    return proxy && proxy.openai.configured && !proxy.anthropic ? 'openai' : settings.provider;
  }

  /**
   * Whether a request can reach a model: the local agent, the training mode, a key of the user's or of the agent, or the
   * editor's server holding one. Without any the chat runs the simulation instead (`simulate`).
   */
  modelAvailable(): boolean {
    const settings = this.state.settings, proxy = this.state.proxy;
    return !!(this.options.provider || settings.localAgent || settings.mock || settings.apiKey || settings.openai?.apiKey
      || this.agent.endpoint?.baseUrl || proxy?.anthropic || proxy?.openai.configured);
  }

  /** The model a request names: the provider's own choice for an OpenAI-compatible API (the server's default when it names one), else the agent's. */
  modelFor(agent: AgentDefinition): string {
    return this.effectiveProvider() === 'openai' ? this.state.settings.openai?.model || this.state.proxy?.openai.model || DEEPSEEK_MODELS[0].id : agentModel(agent, this.state.settings);
  }

  /** What the editor's proxy can do; keys stay on the server, only their presence is reported. */
  async probeProxyConfig(): Promise<ProxyConfig> {
    const doFetch = this.options.fetch ?? fetch;
    const origin = typeof location !== 'undefined' ? location.origin : 'http://127.0.0.1';
    let next: ProxyConfig = null;
    try {
      const response = await doFetch(origin + PROXY_BASE_PATH + '/config');
      if (response.ok) next = await response.json() as ProxyConfig;
    } catch { next = null; }
    if (JSON.stringify(next) !== JSON.stringify(this.state.proxy)) this.set({ proxy: next });
    return next;
  }

  /** Ask the local agent server whether it is up; the panel polls this while the mode is on. */
  async probeLocalAgent(): Promise<LocalAgentStatus> {
    if (!this.state.settings.localAgent) { const off: LocalAgentStatus = { state: 'off' }; this.set({ local: off }); return off; }
    const doFetch = this.options.fetch ?? fetch;
    let next: LocalAgentStatus;
    try {
      const response = await doFetch(this.localAgentUrl + '/status');
      const body = await response.json() as { ok?: boolean; bridge?: boolean; project?: { name: string } | null };
      next = body.ok ? { state: 'ready', project: body.project?.name ?? null, detail: body.bridge ? undefined : t('#AgentLocalNotOnBridge') } : { state: 'offline', detail: t('#AgentLocalNoOk') };
    } catch { next = { state: 'offline', detail: t('#AgentLocalStartHint') }; }
    if (JSON.stringify(next) !== JSON.stringify(this.state.local)) this.set({ local: next });
    return next;
  }

  get settings(): AgentSettings { return this.state.settings; }
  get agents(): AgentDefinition[] { return allAgents(this.state.settings); }
  get agent(): AgentDefinition { return findAgent(this.state.settings, this.state.settings.agentId) ?? BUILT_IN_AGENTS[0]; }

  updateSettings(patch: Partial<AgentSettings>): void {
    const settings = { ...this.state.settings, ...patch };
    if (!findAgent(settings, settings.agentId)) settings.agentId = BUILT_IN_AGENTS[0].id;
    saveAgentSettings(settings, this.options.storage === undefined ? undefined : this.options.storage);
    this.set({ settings });
  }
  setAgent(agentId: string): void { if (findAgent(this.state.settings, agentId)) this.updateSettings({ agentId }); }

  /**
   * A tool call arriving over the bridge. During a local run it belongs to the answer being
   * written (the card the run announced, or a new one); otherwise it is an external agent's.
   */
  noteExternal(name: string, input: unknown): (outcome: ToolOutcome) => void {
    const running = this.localRunTurn;
    if (running) {
      const turn = this.state.turns.find(value => value.id === running);
      const announced = turn?.parts.find((part): part is Extract<ChatPart, { kind: 'tool' }> => part.kind === 'tool' && part.name === name && part.status === 'pending');
      const id = announced?.id ?? newId();
      if (announced) this.updateToolPart(running, id, part => ({ ...part, input, status: 'running' }));
      else this.appendPart(running, { kind: 'tool', id, name, input, status: 'running' }, id);
      return outcome => this.updateToolPart(running, id, part => ({ ...part, status: outcome.isError ? 'error' : 'done', summary: outcome.summary,
        detail: outcome.content.filter(block => block.type === 'text').map(block => (block as { text: string }).text).join('\n').slice(0, 4000), image: dataUrlOf(outcome.content) }));
    }
    const id = newId();
    const part: ChatPart = { kind: 'tool', id, name, input, status: 'running', external: true };
    const last = this.state.turns[this.state.turns.length - 1];
    if (last && last.role === 'external' && last.status === 'streaming') this.patchTurn(last.id, turn => ({ ...turn, parts: [...turn.parts, part] }));
    else this.set({ turns: [...this.state.turns, { id: newId(), role: 'external', agentName: t('#AgentExternalName'), parts: [part], status: 'streaming', startedAt: nowMs() }] });
    return outcome => {
      this.set({ turns: this.state.turns.map(turn => turn.parts.some(p => p.kind === 'tool' && p.id === id)
        ? { ...turn, parts: turn.parts.map(p => p.kind === 'tool' && p.id === id ? { ...p, status: outcome.isError ? 'error' : 'done', summary: outcome.summary,
          detail: outcome.content.filter(block => block.type === 'text').map(block => (block as { text: string }).text).join('\n').slice(0, 4000), image: dataUrlOf(outcome.content) } : p) }
        : turn) });
    };
  }
  /** The bridge went quiet: close the external turn so a later call opens a new one. */
  closeExternalTurn(): void {
    const last = this.state.turns[this.state.turns.length - 1];
    if (last && last.role === 'external' && last.status === 'streaming') this.patchTurn(last.id, turn => ({ ...turn, status: 'done', finishedAt: nowMs() }));
  }

  stop(): void {
    if (!this.abort) return;
    this.abort.abort();
    this.abort = null;
    this.context = null;
    this.set({ turns: this.state.turns.map(turn => turn.role === 'assistant' && turn.status === 'streaming'
      ? { ...turn, status: 'stopped', parts: turn.parts.map(part => part.kind === 'tool' && (part.status === 'pending' || part.status === 'running')
        ? { ...part, status: 'error', summary: t('#AgentActionCancelled'), detail: 'Cancelled by the user.' } : part) } : turn) });
    agentCursor.hide();
  }

  async retry(): Promise<void> {
    const request = this.lastRequest;
    if (!request || this.state.status === 'running') return;
    // Drop the failed answer and its user message, then ask again.
    const turns = this.state.turns.slice();
    const failed = turns[turns.length - 1];
    if (failed && failed.role === 'assistant' && failed.status !== 'done') turns.pop();
    if (turns[turns.length - 1]?.role === 'user') turns.pop();
    const lastHistory = this.history[this.history.length - 1];
    if (lastHistory?.role === 'user' && typeof lastHistory.content !== 'string' && !lastHistory.content.some(block => block.type === 'tool_result')) this.history.pop();
    this.set({ turns });
    await this.send(request.text, request.attachments);
  }

  /** Put the document back to the state before an answer, as one undoable step. */
  rollback(turnId: string): void {
    const turn = this.state.turns.find(value => value.id === turnId);
    if (!turn?.snapshot || turn.rolledBack) return;
    const scope = store.captureDocumentScope(), before = turn.snapshotScope;
    const invalidated = turn.rollbackInvalidated || !scope || !before || scope.projectId !== before.projectId || scope.epoch !== before.epoch
      || scope.revision !== turn.lastOwnRevision;
    const note = invalidated ? rollbackNote(turn.rollbackReason ?? 'changed')
      : this.state.status === 'running' ? rollbackNote('running') : store.documentBusy ? rollbackNote('busy') : undefined;
    if (note) { this.patchTurn(turnId, value => ({ ...value, rollbackNote: note,
      ...(invalidated ? { rollbackInvalidated: true, rollbackReason: value.rollbackReason ?? 'changed' } : {}) })); return; }
    const commandId = newId();
    store.withDocumentOwner({ turnId, commandId }, () => store.restoreScene(turn.snapshot!));
    if (store.isShared) {
      void store.awaitDocumentCommand(commandId).then(decision => {
        this.patchTurn(turnId, value => decision.status === 'committed' || decision.status === 'unchanged'
          ? { ...value, rolledBack: true, edits: turn.edits, rollbackNote: undefined }
          : { ...value, rollbackNote: decision.status === 'pending' ? t('#AgentRollbackChecking') : rollbackNote('changed') });
        if (decision.status === 'pending') {
          const stop = store.subscribe(() => {
            const current = store.captureDocumentScope();
            if (!current || current.projectId !== scope!.projectId || current.epoch !== scope!.epoch) { stop(); return; }
            void store.awaitDocumentCommand(commandId).then(result => {
              if (result.status === 'pending') return;
              stop();
              this.patchTurn(turnId, value => result.status === 'committed' || result.status === 'unchanged'
                ? { ...value, rolledBack: true, edits: turn.edits, rollbackNote: undefined }
                : { ...value, rollbackNote: rollbackNote('changed') });
            });
          });
        }
      });
    } else this.patchTurn(turnId, value => ({ ...value, rolledBack: true, edits: turn.edits, rollbackNote: undefined }));
  }

  async send(text: string, attachments: string[] = []): Promise<void> {
    const message = text.trim();
    if (!message && !attachments.length) return;
    if (this.state.status === 'running') return;
    // No model to ask: the agents show the work on the project instead, and say so. The server may have answered late.
    if (!this.modelAvailable()) {
      await this.probeProxyConfig();
      if (this.get().status === 'running') return;
      if (!this.modelAvailable()) { this.set({ draft: '' }); await this.simulate(message || t('#AgentSimLookRequest')); return; }
    }
    const editorState = store.get();
    const agent = this.agent;
    this.lastRequest = { text: message, attachments };
    const userTurn: ChatTurn = { id: newId(), role: 'user', parts: [{ kind: 'text', text: message }], attachments, status: 'done', startedAt: nowMs() };
    const answer: ChatTurn = { id: newId(), role: 'assistant', agentId: agent.id, agentName: agent.name, parts: [], status: 'streaming', startedAt: nowMs(),
      snapshot: editorState.scene, snapshotProjectId: editorState.projectId, snapshotScope: store.captureDocumentScope(),
      lastOwnRevision: store.captureDocumentScope()?.revision, edits: 0 };
    this.closeExternalTurn();
    this.set({ turns: [...this.state.turns, userTurn, answer], status: 'running', draft: '' });
    if (!editorState.scene) {
      this.patchTurn(answer.id, turn => ({ ...turn, status: 'error', finishedAt: nowMs(), error: { message: t('#AgentOpenProjectFirst'), retryable: false } }));
      this.set({ status: 'idle' });
      return;
    }
    const abort = new AbortController();
    this.abort = abort;
    agentCursor.setAgent(agent.name);
    const content: ContentBlockParam[] = [
      ...attachments.map(dataUrl => parseDataUrl(dataUrl)).filter((image): image is NonNullable<typeof image> => !!image),
      { type: 'text', text: `Editor state now (automatic):\n${briefScene(store.get())}\n\nUser: ${message || '(see the attached picture)'}` },
    ];
    if (!this.state.settings.localAgent) this.history.push({ role: 'user', content });
    try {
      if (this.state.settings.localAgent) await this.runLocal(agent, message, attachments.length, answer.id, abort.signal);
      else await this.runLoop(agent, this.history, answer.id, abort.signal, 0);
      this.patchTurn(answer.id, turn => ({ ...turn, status: abort.signal.aborted ? 'stopped' : turn.status === 'streaming' ? 'done' : turn.status }));
    } catch (error) {
      const described = error instanceof LocalAgentError ? { message: error.message, retryable: error.retryable }
        : error instanceof ProviderHttpError ? { message: error.message, retryable: error.status === 0 || error.status === 429 || error.status >= 500 } : describeProviderError(error);
      this.patchTurn(answer.id, turn => ({ ...turn, status: abort.signal.aborted ? 'stopped' : 'error', error: abort.signal.aborted ? undefined : described }));
    } finally {
      const ownsRun = this.abort === abort || (!this.abort && this.state.turns.filter(turn => turn.role === 'assistant').at(-1)?.id === answer.id);
      if (ownsRun) {
        this.localRunTurn = null;
        agentCursor.done();
        this.abort = null;
        this.set({ status: 'idle' });
      }
      this.patchTurn(answer.id, turn => ({ ...turn, finishedAt: nowMs() }));
    }
  }

  /** A request and the answer that will hold a run of the chat's own (the simulation, the health check). */
  private openRun(request: string, agentName: string): { answer: ChatTurn; abort: AbortController } | null {
    if (this.state.status === 'running') return null;
    const userTurn: ChatTurn = { id: newId(), role: 'user', parts: [{ kind: 'text', text: request }], status: 'done', startedAt: nowMs() };
    const answer: ChatTurn = { id: newId(), role: 'assistant', agentName, parts: [], status: 'streaming', startedAt: nowMs(), edits: 0 };
    this.closeExternalTurn();
    this.lastRequest = null;
    this.set({ turns: [...this.state.turns, userTurn, answer], status: 'running', draft: '', view: 'chat' });
    const abort = new AbortController();
    this.abort = abort;
    return { answer, abort };
  }
  private closeRun(answer: ChatTurn, abort: AbortController): void {
    if (this.abort === abort) this.abort = null;
    this.patchTurn(answer.id, turn => ({ ...turn, status: turn.status === 'streaming' ? (abort.signal.aborted ? 'stopped' : 'done') : turn.status, finishedAt: nowMs() }));
    this.set({ status: 'idle' });
  }
  /** Borrows the bots for a run: they come now and leave a moment after it, unless the user asked for them. */
  private borrowBots(): { ready: Promise<{ direct(botId: string, task: DemoTask | null): boolean } | null>; giveBack: () => void } {
    const bots = this.options.demo ?? demoBots, release = bots.lease();
    return { ready: bots.ready(1500), giveBack: () => { setTimeout(release, this.options.botsLinger ?? 6000); } };
  }

  /**
   * The agents at work without a model (`simulation.ts`, the design's «Subagents»): four subagents side by side on the
   * open project, their steps in the chat as they go and their pointers on the stage on what each works on. Nothing is
   * written to the document; the answer says so.
   */
  async simulate(text = t('#AgentSimDefaultRequest')): Promise<void> {
    const request = text.trim() || t('#AgentSimDefaultRequest');
    const run = this.openRun(request, t('#AgentSimName'));
    if (!run) return;
    const { answer, abort } = run;
    const level = store.level;
    if (!store.get().scene || !level) {
      this.patchTurn(answer.id, turn => ({ ...turn, status: 'error', error: { message: t('#AgentSimOpenProjectFirst'), retryable: false } }));
      this.closeRun(answer, abort);
      return;
    }
    const bots = this.borrowBots(), lastTarget = new Map<string, DemoTask['target']>();
    let demo: Awaited<typeof bots.ready> = null;
    try {
      demo = await abortable(bots.ready, abort.signal);
      const plan = planSimulation(level, request, this.options.catalogLookup ?? catalogLookup);
      const outcomes = await runSimulation(plan, {
        begin: (agent, stepId, step) => {
          this.appendPart(answer.id, { kind: 'tool', id: stepId, name: 'simulation_step', input: { agent: agent.id }, status: 'running', progress: step.running, agent: agent.name }, stepId);
          if (step.target) { lastTarget.set(agent.id, step.target); demo?.direct(agent.id, { target: step.target, activity: step.running, hold: step.hold }); }
        },
        finish: (_agent, stepId, outcome) => this.updateToolPart(answer.id, stepId, part => ({ ...part, status: outcome.status, summary: outcome.summary, detail: outcome.detail })),
        // A finished agent rests on its last work until the others are through, as the design's done cards keep their pointers.
        release: agent => { const target = lastTarget.get(agent.id); if (target && !abort.signal.aborted) demo?.direct(agent.id, { target, activity: t('Done') }); },
      }, abort.signal, this.options.simulationPace ?? 1);
      this.appendPart(answer.id, { kind: 'text', text: simulationAnswer(plan, outcomes) }, newId());
    } catch (error) {
      if (!abort.signal.aborted) this.patchTurn(answer.id, turn => ({ ...turn, status: 'error', error: { message: t('#AgentSimFailed', reason(error)), retryable: false } }));
    } finally {
      // The agents stay a moment on what they did, then roam again — or leave, when only this run had called them.
      const agents = demo, stopped = abort.signal.aborted;
      const letGo = () => { for (const agent of DEMO_AGENTS) agents?.direct(agent.id, null); };
      if (stopped) letGo(); else setTimeout(letGo, this.options.botsLinger ?? 6000);
      bots.giveBack();
      this.closeRun(answer, abort);
    }
  }

  /**
   * «Проверка работоспособности»: what an agent run stands on, one part at a time — the editor's agent server, the
   * model, the project read through the agent's own tool, the catalog, the materials, a picture of the view and the
   * bots — each ticked or failed in the chat with what it answered, then a count.
   */
  async checkHealth(): Promise<void> {
    const run = this.openRun(t('#AgentHealthRequest'), t('#AgentHealthName'));
    if (!run) return;
    const { answer, abort } = run;
    this.patchTurn(answer.id, turn => ({ ...turn, expanded: true }));
    const results: { label: string; ok: boolean }[] = [];
    const check = async (label: string, running: string, work: () => Promise<string>) => {
      if (abort.signal.aborted) return;
      const id = newId();
      this.appendPart(answer.id, { kind: 'tool', id, name: 'health_check', input: { check: label }, status: 'running', progress: running }, id);
      try {
        const summary = await abortable(work(), abort.signal);
        results.push({ label, ok: true });
        this.updateToolPart(answer.id, id, part => ({ ...part, status: 'done', summary: `${label}: ${summary}` }));
      } catch (error) {
        if (abort.signal.aborted) return;
        results.push({ label, ok: false });
        this.updateToolPart(answer.id, id, part => ({ ...part, status: 'error', summary: `${label}: ${reason(error)}` }));
      }
    };
    const timed = async <T,>(work: () => Promise<T>): Promise<[T, number]> => { const started = nowMs(); const value = await work(); return [value, nowMs() - started]; };
    const bots = this.borrowBots();
    try {
      await check(t('#AgentHealthServer'), t('#AgentHealthServerRunning'), async () => {
        const proxy = await this.probeProxyConfig();
        if (!proxy) throw new Error(t('#AgentHealthServerDown'));
        const keys = [proxy.anthropic && 'Anthropic', proxy.openai.configured && (proxy.openai.baseUrl?.includes('deepseek') || !proxy.openai.baseUrl ? 'DeepSeek' : t('#AgentHealthOpenAICompatible'))].filter(Boolean);
        return keys.length ? t('#AgentHealthServerKeys', keys.join(', ')) : t('#AgentHealthServerNoKey');
      });
      await check(t('#AgentHealthModel'), t('#AgentHealthModelRunning'), () => this.pingModel(abort.signal));
      await check(t('#AgentHealthProject'), t('#AgentHealthProjectRunning'), async () => {
        const services = this.services(this.agent, answer.id, abort.signal, 1);
        const outcome = await runTool('get_scene', {}, { ...services, agentName: t('#AgentHealthName'), command: { commandId: newId(), callerId: 'agent-health', turnId: answer.id } });
        if (outcome.isError) throw new Error(outcome.summary || t('#AgentHealthToolSilent'));
        const level = store.level;
        return level ? t('#AgentHealthProjectCounts', plural('#AgentCountRooms', level.rooms.length), plural('#AgentCountWalls', level.walls.length), plural('#AgentCountItems', level.items.length)) : outcome.summary;
      });
      await check(t('#AgentHealthCatalog'), t('#AgentHealthCatalogRunning'), async () => {
        const [list, ms] = await timed(() => (this.options.catalogLookup ?? catalogLookup)('Sofa', abort.signal));
        if (!list) throw new Error(t('#AgentHealthNoSofas'));
        return t('#AgentHealthCatalogAnswered', seconds(ms), `${list.items.length}${list.more ? '+' : ''}`, list.title);
      });
      await check(t('Materials'), t('#AgentHealthMaterialsRunning'), async () => {
        const section = MATERIAL_SECTIONS[0];
        if (!section) throw new Error(t('#AgentHealthNoMaterialSections'));
        const [page, ms] = await timed(() => loadMaterialPage(section, firstMaterialCursor()));
        return t('#AgentHealthMaterialsAnswered', seconds(ms), page.items.length, section.title);
      });
      await check(t('#AgentHealthSnapshot'), t('#AgentHealthSnapshotRunning'), async () => {
        const editor = (this.options.editor ?? editorAccess)();
        if (!editor) throw new Error(t('#AgentHealthNoView'));
        const picture = await captureViewImage(editor, 320);
        if (!picture) throw new Error(t('#AgentHealthNoFrame'));
        // The picture's label is written for the model («2D plan (floor)»); the chat names the view in the interface language.
        const view = picture.label.startsWith('2D') ? t('#AgentHealthViewPlan') : picture.label.includes('walk') ? t('#AgentHealthViewWalk') : t('#AgentHealthViewOrbit');
        return t('#AgentHealthSnapshotTaken', view, Math.max(1, Math.round(picture.blob.size / 1024)));
      });
      await check(t('#AgentHealthBots'), t('#AgentHealthBotsRunning'), async () => {
        const demo = await bots.ready;
        if (!demo) throw new Error(t('#AgentHealthBotsUnavailable'));
        return plural('#AgentHealthBotsReady', DEMO_AGENTS.length);
      });
      if (!abort.signal.aborted) {
        const failed = results.filter(result => !result.ok), noModel = !this.modelAvailable();
        this.appendPart(answer.id, { kind: 'text', text: [
          t('#AgentHealthSummary', results.length - failed.length, results.length),
          failed.length ? t('#AgentHealthFailedList', failed.map(result => result.label.toLowerCase()).join(', ')) : t('#AgentHealthAllOk'),
          noModel ? t('#AgentHealthNoModelNote') : '',
        ].filter(Boolean).join(' ') }, newId());
      }
    } finally {
      bots.giveBack();
      this.closeRun(answer, abort);
    }
  }

  /** A one-word request to the model the chat would use, or what stands in its way. */
  private async pingModel(signal: AbortSignal): Promise<string> {
    const settings = this.state.settings;
    if (settings.mock) return t('#AgentHealthMockMode');
    if (settings.localAgent) {
      const status = await this.probeLocalAgent();
      if (status.state !== 'ready') throw new Error(status.detail ? t('#AgentHealthLocalDownDetail', status.detail) : t('#AgentHealthLocalDown'));
      return status.project ? t('#AgentHealthLocalUpProject', status.project) : t('#AgentHealthLocalUp');
    }
    if (!this.modelAvailable()) throw new Error(t('#AgentHealthModelMissing'));
    const agent = this.agent, model = this.modelFor(agent), started = nowMs();
    const message = await this.provider(agent).run({ model, system: [{ type: 'text', text: 'Health check of the editor. Reply with the single word OK.' }],
      messages: [{ role: 'user', content: 'OK?' }], tools: [], maxTokens: 16, signal }).final();
    const took = seconds(nowMs() - started);
    return textOf(message) ? t('#AgentHealthModelAnsweredText', model, took, textOf(message).slice(0, 24)) : t('#AgentHealthModelAnswered', model, took);
  }

  /**
   * One message through the local agent: Claude Code runs the loop on the user's
   * subscription and calls the editor over the bridge; this only paints what it streams.
   */
  private async runLocal(agent: AgentDefinition, message: string, attachments: number, turnId: string, signal: AbortSignal): Promise<void> {
    const context = await abortable(this.promptContext(), signal);
    const system = buildSystemBlocks(agent, { ...context, agents: this.agents, vision: true }).map(block => block.text).join('\n\n')
      + '\n\nThe editor tools are MCP tools named mcp__home-planner__<tool>; delegate is not available in this mode. Call editor_status first if a tool says the editor is not connected.';
    const prompt = `Editor state now (automatic):\n${briefScene(store.get())}\n\n${attachments ? 'The user attached a picture of the current view; call capture_view to see it.\n\n' : ''}User: ${message || '(see the current view)'}`;
    const doFetch = this.options.fetch ?? fetch;
    let response: Response;
    try {
      response = await doFetch(this.localAgentUrl + '/run', { method: 'POST', headers: { 'content-type': 'application/json' }, signal,
        body: JSON.stringify({ message: prompt, system, model: this.state.settings.localModel || undefined, sessionId: this.localSessions.get(agent.id) }) });
    } catch (error) {
      if (signal.aborted) return;
      throw new LocalAgentError(t('#AgentLocalNotRunning'), true, error);
    }
    if (!response.ok || !response.body) throw new LocalAgentError(t('#AgentLocalHttpError', String(response.status)), true);
    this.localRunTurn = turnId;
    this.set({ local: { ...this.state.local, state: 'ready' } });
    let textKey: string | null = null;
    const label = undefined;
    for await (const event of readSse(response.body, signal)) {
      if (signal.aborted) break;
      if (event.type === 'started') { if (event.sessionId) this.localSessions.set(agent.id, event.sessionId); }
      else if (event.type === 'text') {
        if (!textKey) { textKey = newId(); this.appendPart(turnId, { kind: 'text', text: '', agent: label }, textKey); }
        this.growPart(turnId, textKey, event.text);
      } else if (event.type === 'thinking') {
        const key = 'thinking-' + turnId;
        const turn = this.state.turns.find(value => value.id === turnId);
        if (!turn?.parts.some(part => (part as { id?: string }).id === key)) this.appendPart(turnId, { kind: 'thinking', text: '' }, key);
        this.growPart(turnId, key, event.text);
      } else if (event.type === 'tool_use') {
        textKey = null;
        this.appendPart(turnId, { kind: 'tool', id: event.id, name: event.name, input: event.input, status: event.name === 'editor_status' ? 'running' : 'pending' }, event.id);
      } else if (event.type === 'tool_result') {
        this.updateToolPart(turnId, event.id, part => part.status === 'done' || part.status === 'error' ? part
          : { ...part, status: event.isError ? 'error' : 'done', summary: part.summary ?? (event.isError ? event.text.slice(0, 200) : part.name === 'editor_status' ? t('#AgentDoneEditorStatus') : t('#AgentStepDone')), detail: event.text });
      } else if (event.type === 'error') {
        throw new LocalAgentError(localErrorMessage(event.message), true);
      } else if (event.type === 'result') {
        if (event.sessionId) this.localSessions.set(agent.id, event.sessionId);
        if (event.isError) throw new LocalAgentError(localErrorMessage(event.text), true);
        const turn = this.state.turns.find(value => value.id === turnId);
        if (event.text && !turn?.parts.some(part => part.kind === 'text' && part.text.trim())) this.appendPart(turnId, { kind: 'text', text: event.text }, newId());
      }
    }
  }

  private provider(agent: AgentDefinition): AgentProvider {
    const settings = this.state.settings;
    if (this.options.provider) return this.options.provider(agent, settings);
    if (settings.mock) return new MockProvider(undefined, this.options.mockPace ?? 12);
    if (this.effectiveProvider() === 'openai') {
      const origin = typeof location !== 'undefined' ? location.origin : 'http://127.0.0.1';
      const key = settings.openai?.apiKey;
      return new OpenAICompatibleProvider({ baseURL: key ? (settings.openai?.baseUrl || DEEPSEEK_BASE_URL) : origin + PROXY_BASE_PATH + '/openai/v1', apiKey: key, fetch: this.options.fetch, thinking: !!settings.openai?.thinking });
    }
    if (agent.custom && agent.endpoint?.baseUrl) return new ClaudeProvider({ baseURL: agent.endpoint.baseUrl.replace(/\/$/, ''), apiKey: agent.endpoint.apiKey || settings.apiKey || 'proxy' });
    if (settings.apiKey) return new ClaudeProvider({ apiKey: settings.apiKey });
    const origin = typeof location !== 'undefined' ? location.origin : 'http://127.0.0.1';
    return new ClaudeProvider({ baseURL: origin + PROXY_BASE_PATH });
  }

  private promptContext() {
    this.context ??= Promise.all([
      (this.options.catalog ?? loadAgentCatalog)().catch(() => ({ sections: [], items: [] } as Catalog)),
      (this.options.materials ?? loadAgentMaterials)().catch(() => [] as MaterialEntry[]),
    ]).then(([catalog, materials]) => ({ catalog, materials }));
    return this.context;
  }

  private toolParams(agent: AgentDefinition, depth: number): ToolParam[] {
    return toolsInGroups(agent.tools).filter(tool => depth === 0 || tool.group !== 'agents')
      .map(tool => ({ name: tool.name, description: tool.description, input_schema: tool.input_schema as ToolParam['input_schema'] }));
  }

  private services(agent: AgentDefinition, turnId: string, signal: AbortSignal, depth: number): ToolServices {
    const editor = this.options.editor ?? editorAccess;
    return {
      catalog: this.options.catalog ?? loadAgentCatalog, materials: this.options.materials, editor, app: this.options.app ?? appAccess, signal,
      render: this.options.render ?? ((input, abortSignal) => {
        const access = editor();
        if (!access) return Promise.reject(new Error('The editor view is not available.'));
        return renderThroughService(input, access, abortSignal);
      }),
      delegate: depth === 0 ? (agentId, task) => this.delegate(agent, agentId, task, turnId, signal) : undefined,
      agentName: agent.name,
    };
  }

  private async delegate(from: AgentDefinition, agentId: string, task: string, turnId: string, signal: AbortSignal): Promise<string> {
    const target = findAgent(this.state.settings, agentId);
    if (!target) throw new Error(`No agent "${agentId}". Agents: ${this.agents.filter(agent => agent.id !== from.id).map(agent => agent.id).join(', ')}.`);
    if (target.id === from.id) throw new Error('An agent cannot delegate to itself.');
    const history: MessageParam[] = [{ role: 'user', content: `Editor state now (automatic):\n${briefScene(store.get())}\n\nTask from ${from.name}: ${task}` }];
    const report = await this.runLoop(target, history, turnId, signal, 1);
    return report || `${target.name} finished without a written report.`;
  }

  /** One conversation with one agent until it stops asking for tools; returns its last words. */
  private async runLoop(agent: AgentDefinition, history: MessageParam[], turnId: string, signal: AbortSignal, depth: number): Promise<string> {
    const context = await abortable(this.promptContext(), signal);
    const system = buildSystemBlocks(agent, { ...context, agents: this.agents, vision: this.effectiveProvider() !== 'openai' });
    const tools = this.toolParams(agent, depth);
    const provider = this.provider(agent);
    const services = this.services(agent, turnId, signal, depth);
    const loopId = newId();
    const label = depth ? agent.name : undefined;
    let finalText = '';
    for (let iteration = 0; iteration < (this.options.maxIterations ?? MAX_ITERATIONS); iteration++) {
      if (signal.aborted) break;
      const callId = (id: string) => `${loopId}:${iteration}:${id}`;
      const run = provider.run({ model: this.modelFor(agent), system, messages: history, tools, maxTokens: MAX_TOKENS, effort: agent.effort, signal });
      const partIds = new Map<number, string>();
      let message: Message;
      try {
        for await (const event of run.events) {
          if (signal.aborted) break;
          if (event.type === 'content_block_start') {
            const block = event.content_block;
            const id = newId();
            partIds.set(event.index, id);
            if (block.type === 'text') this.appendPart(turnId, { kind: 'text', text: block.text, agent: label }, id);
            else if (block.type === 'thinking') this.appendPart(turnId, { kind: 'thinking', text: '', agent: label }, id);
            else if (block.type === 'tool_use') this.appendPart(turnId, { kind: 'tool', id: callId(block.id), name: block.name, input: {}, status: 'pending', agent: label }, id);
          } else if (event.type === 'content_block_delta') {
            const id = partIds.get(event.index);
            if (!id) continue;
            if (event.delta.type === 'text_delta') this.growPart(turnId, id, event.delta.text);
            else if (event.delta.type === 'thinking_delta') this.growPart(turnId, id, event.delta.thinking);
          }
        }
        message = await run.final();
      } catch (error) {
        if (signal.aborted) return finalText;
        throw error;
      }
      history.push({ role: 'assistant', content: message.content });
      finalText = textOf(message) || finalText;
      // Bind the streamed tool cards to the finished blocks: the inputs arrive whole only here.
      for (const block of message.content) if (block.type === 'tool_use') this.updateToolPart(turnId, callId(block.id), part => ({ ...part, input: block.input }));
      if (message.stop_reason === 'refusal') {
        this.patchTurn(turnId, turn => ({ ...turn, status: 'error', error: { message: t('#AgentModelRefused'), retryable: false } }));
        break;
      }
      const uses = message.content.filter((block): block is Extract<Message['content'][number], { type: 'tool_use' }> => block.type === 'tool_use');
      if (!uses.length || message.stop_reason !== 'tool_use') {
        if (message.stop_reason === 'max_tokens') this.appendPart(turnId, { kind: 'text', text: '\n\n' + t('#AgentAnswerTruncated'), agent: label }, newId());
        break;
      }
      const results: ToolResultBlockParam[] = [];
      for (const use of uses) {
        if (signal.aborted) { results.push({ type: 'tool_result', tool_use_id: use.id, content: 'Cancelled by the user.', is_error: true }); continue; }
        this.updateToolPart(turnId, callId(use.id), part => ({ ...part, status: 'running' }));
        let outcome = await runTool(use.name, use.input, { ...services, allowedTools: tools.map(tool => tool.name),
          command: { commandId: callId(use.id), callerId: 'agent-session', turnId } });
        if (signal.aborted && outcome.command?.status !== 'committed' && outcome.command?.status !== 'pending') outcome = { ...outcome, isError: true, summary: t('#AgentActionCancelled'), content: [{ type: 'text', text: 'Cancelled by the user.' }] };
        this.updateToolPart(turnId, callId(use.id), part => ({ ...part, status: outcome.isError ? 'error' : 'done', summary: outcome.summary,
          detail: outcome.content.filter(block => block.type === 'text').map(block => (block as { text: string }).text).join('\n').slice(0, 4000), image: dataUrlOf(outcome.content) }));
        results.push({ type: 'tool_result', tool_use_id: use.id, content: toApiContent(outcome.content), ...(outcome.isError ? { is_error: true } : {}) });
      }
      history.push({ role: 'user', content: results });
      if (signal.aborted) break;
    }
    return finalText;
  }

  private appendPart(turnId: string, part: ChatPart, key: string): void {
    this.patchTurn(turnId, turn => ({ ...turn, parts: [...turn.parts, { ...part, ...(part.kind === 'tool' ? {} : { id: key }) } as ChatPart & { id?: string }] }));
  }
  private growPart(turnId: string, key: string, text: string): void {
    this.patchTurn(turnId, turn => ({ ...turn, parts: turn.parts.map(part => (part as { id?: string }).id === key && (part.kind === 'text' || part.kind === 'thinking') ? { ...part, text: part.text + text } : part) }));
  }
  private updateToolPart(turnId: string, toolUseId: string, update: (part: Extract<ChatPart, { kind: 'tool' }>) => ChatPart): void {
    this.patchTurn(turnId, turn => ({ ...turn, parts: turn.parts.map(part => part.kind === 'tool' && part.id === toolUseId ? update(part) : part) }));
  }
}

class LocalAgentError extends Error {
  constructor(message: string, readonly retryable: boolean, readonly cause?: unknown) { super(message); this.name = 'LocalAgentError'; }
}
/** Claude Code's own words, turned into what the user can do about them. */
export function localErrorMessage(text: string): string {
  if (/not logged in|\/login/i.test(text)) return t('#AgentLocalNotLoggedIn');
  if (/rate limit|usage limit|limit reached/i.test(text)) return t('#AgentLocalLimit');
  return text.trim() || t('#AgentLocalFailed');
}

function parseDataUrl(dataUrl: string): ContentBlockParam | null {
  const match = /^data:(image\/(?:jpeg|png|webp|gif));base64,(.+)$/s.exec(dataUrl);
  return match ? { type: 'image', source: { type: 'base64', media_type: match[1] as 'image/jpeg' | 'image/png' | 'image/webp' | 'image/gif', data: match[2] } } : null;
}

let shared: AgentSession | null = null;
/** The tab's one session, created on first use so tests can build their own. */
export function agentSession(): AgentSession {
  shared ??= new AgentSession();
  // A console handle beside `hpStore`: the transcript is the only other place the loop's state is visible.
  if (typeof window !== 'undefined' && (import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV) (window as unknown as { hpAgent?: AgentSession }).hpAgent = shared;
  return shared;
}
